import { useState, useEffect } from "react";
import {
  MessageSquare,
  Clock,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Eye,
} from "lucide-react";
import { supabase } from "../providers/AuthProvider";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { SupportChat } from "../components/support/SupportChat";
import { OnlineAdmins } from "../components/support/OnlineAdmins";

interface Ticket {
  id: string;
  user_id: string;
  subject: string;
  description: string;
  status: "open" | "in_progress" | "resolved" | "closed";
  priority: "low" | "medium" | "high" | "urgent";
  assigned_to?: string;
  created_at: string;
  updated_at: string;
  user?: {
    email: string;
  };
}

export function Support() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("support_tickets")
        .select(
          `
          *,
          user:user_id (
            email
          )
        `,
        )
        .order("created_at", { ascending: false });

      if (error) throw error;

      setTickets((data || []) as Ticket[]);
 
// eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error("Error fetching tickets:", error);
      toast.error("Erro ao carregar tickets: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const updateTicketStatus = async (
    ticketId: string,
    status: Ticket["status"],
  ) => {
    try {
      const { error } = await supabase
        .from("support_tickets")
        .update({ status, updated_at: new Date().toISOString() })
        .eq("id", ticketId);

      if (error) throw error;

      toast.success("Status atualizado!");
 
      fetchTickets();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      toast.error("Erro ao atualizar: " + error.message);
    }
  };

  const statusColors: Record<string, string> = {
    open: "bg-red-500/20 text-red-400",
    in_progress: "bg-blue-500/20 text-blue-400",
    resolved: "bg-emerald-500/20 text-emerald-400",
    closed: "bg-slate-500/20 text-slate-400",
  };

  const priorityColors: Record<string, string> = {
    urgent: "text-red-400",
    high: "text-orange-400",
    medium: "text-amber-400",
    low: "text-slate-400",
  };

  const openTickets = tickets.filter(
    (t) => t.status === "open" || t.status === "in_progress",
  ).length;
  const resolvedToday = tickets.filter((t) => {
    if (t.status !== "resolved") return false;
    const resolvedDate = new Date(t.updated_at);
    const today = new Date();
    return resolvedDate.toDateString() === today.toDateString();
  }).length;
  const urgentTickets = tickets.filter(
    (t) => t.priority === "urgent" && t.status !== "resolved",
  ).length;

  // Calcular tempo médio de resposta
  const calculateAvgResponseTime = () => {
    const ticketsWithMessages = tickets.filter((t) => {
      // Assumindo que tickets com status 'in_progress' ou 'resolved' têm pelo menos uma resposta
      return (
        (t.status === "in_progress" || t.status === "resolved") && t.updated_at
      );
    });

    if (ticketsWithMessages.length === 0) return "0h";

    const totalMinutes = ticketsWithMessages.reduce((acc, ticket) => {
      const created = new Date(ticket.created_at).getTime();
      const updated = new Date(ticket.updated_at).getTime();
      const diffMinutes = (updated - created) / (1000 * 60);
      return acc + diffMinutes;
    }, 0);

    const avgMinutes = totalMinutes / ticketsWithMessages.length;
    if (avgMinutes < 60) return `${Math.round(avgMinutes)}m`;
    return `${(avgMinutes / 60).toFixed(1)}h`;
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Suporte</h1>
          <p className="text-slate-400">Central de atendimento ao cliente</p>
        </div>
        <OnlineAdmins />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
          <MessageSquare className="w-8 h-8 text-blue-400 mb-3" />
          <p className="text-sm text-slate-400">Tickets Abertos</p>
          <p className="text-3xl font-bold text-white">{openTickets}</p>
        </div>
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
          <Clock className="w-8 h-8 text-amber-400 mb-3" />
          <p className="text-sm text-slate-400">Tempo Médio Resposta</p>
          <p className="text-3xl font-bold text-white">
            {calculateAvgResponseTime()}
          </p>
        </div>
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
          <CheckCircle className="w-8 h-8 text-emerald-400 mb-3" />
          <p className="text-sm text-slate-400">Resolvidos Hoje</p>
          <p className="text-3xl font-bold text-white">{resolvedToday}</p>
        </div>
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
          <AlertCircle className="w-8 h-8 text-red-400 mb-3" />
          <p className="text-sm text-slate-400">Urgentes</p>
          <p className="text-3xl font-bold text-white">{urgentTickets}</p>
        </div>
      </div>

      {selectedTicketId ? (
        <div className="h-[600px]">
          <SupportChat
            ticketId={selectedTicketId}
            onClose={() => {
              setSelectedTicketId(null);
              fetchTickets();
            }}
          />
        </div>
      ) : (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl overflow-hidden">
          <div className="p-6 border-b border-white/10 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">Tickets</h3>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="w-8 h-8 animate-spin text-red-500" />
            </div>
          ) : tickets.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400">
              <MessageSquare className="w-12 h-12 mb-4 opacity-50" />
              <p>Nenhum ticket encontrado</p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-4 px-6 text-xs font-medium text-slate-400 uppercase">
                    Cliente
                  </th>
                  <th className="text-left py-4 px-6 text-xs font-medium text-slate-400 uppercase">
                    Assunto
                  </th>
                  <th className="text-left py-4 px-6 text-xs font-medium text-slate-400 uppercase">
                    Status
                  </th>
                  <th className="text-left py-4 px-6 text-xs font-medium text-slate-400 uppercase">
                    Prioridade
                  </th>
                  <th className="text-left py-4 px-6 text-xs font-medium text-slate-400 uppercase">
                    Criado
                  </th>
                  <th className="text-right py-4 px-6 text-xs font-medium text-slate-400 uppercase">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {tickets.map((ticket) => (
                  <tr
                    key={ticket.id}
                    className="hover:bg-white/5 cursor-pointer"
                    onClick={() => setSelectedTicketId(ticket.id)}
                  >
                    <td className="py-4 px-6 text-white font-medium">
                      {ticket.user?.email || ticket.user_id.slice(0, 8)}
                    </td>
                    <td className="py-4 px-6 text-slate-400">
                      {ticket.subject}
                    </td>
                    <td className="py-4 px-6">
                      <select
                        value={ticket.status}
                        onChange={(e) => {
                          e.stopPropagation();
                          updateTicketStatus(
                            ticket.id,
                            e.target.value as Ticket["status"],
                          );
                        }}
                        className={`px-2.5 py-1 rounded-full text-xs capitalize border-0 bg-transparent ${statusColors[ticket.status]}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="open">Aberto</option>
                        <option value="in_progress">Em Progresso</option>
                        <option value="resolved">Resolvido</option>
                        <option value="closed">Fechado</option>
                      </select>
                    </td>
                    <td className="py-4 px-6">
                      <span
                        className={`text-sm capitalize ${priorityColors[ticket.priority]}`}
                      >
                        {ticket.priority === "urgent"
                          ? "Urgente"
                          : ticket.priority === "high"
                            ? "Alta"
                            : ticket.priority === "medium"
                              ? "Média"
                              : "Baixa"}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-slate-400 text-sm">
                      {new Date(ticket.created_at).toLocaleDateString("pt-BR")}
                    </td>
                    <td className="py-4 px-6 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTicketId(ticket.id);
                          }}
                          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                          title="Abrir chat"
                        >
                          <MessageSquare className="w-4 h-4 text-emerald-400" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/clients/${ticket.user_id}`);
                          }}
                          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                          title="Ver cliente"
                        >
                          <Eye className="w-4 h-4 text-slate-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
